import Enums from '@core/types/api/Enums';
import { NIL_UUID } from '@core/utils/uuid';

import type { TableDataModel } from '../components/DataTable/TableDataModel';
import type { FilterByOpts } from './ApiClient';
import type { BacklogFiltersFormikType } from './backlog';

/** The page-level Backlog filter fields this module knows how to translate. */
export type BacklogFilterSelection = Pick<BacklogFiltersFormikType, 'markets' | 'salespersons'>;

const MARKET_FILTER_NAME = 'marketId';
const SALESPERSON_FILTER_NAME = 'userId';

/**
 * `marketId` and `userId` are filterable on every Backlog endpoint but are not
 * fields on the generated row types, so the filter name needs a cast. This is
 * the only place in the codebase that cast lives — call sites stay clean.
 *
 * All selected IDs go into a SINGLE entry's `values`, which the backend treats
 * as OR. Emitting one entry per selected ID would be wrong: the backend
 * intersects repeated same-name filters (internal/server/actuals.go:86-89), so
 * two marketId entries require a project to be in BOTH markets and almost
 * always return nothing.
 */
const namedFilter = <TRow extends { id: string }>(
  name: string,
  values: string[],
): FilterByOpts<TRow> =>
  ({
    name,
    operation: Enums.FilterOperation.Equals,
    values,
  }) as FilterByOpts<TRow>;

/**
 * Translates the page-level Backlog filter selection into query filters for
 * `useSlabQuery`-style consumers (metric cards, alerts).
 */
export const backlogQueryFilters = <TRow extends { id: string }>(
  selection: BacklogFilterSelection,
): FilterByOpts<TRow>[] => {
  const filters: FilterByOpts<TRow>[] = [];

  if (selection.markets.length > 0) {
    filters.push(namedFilter<TRow>(MARKET_FILTER_NAME, selection.markets));
  }
  if (selection.salespersons.length > 0) {
    filters.push(namedFilter<TRow>(SALESPERSON_FILTER_NAME, selection.salespersons));
  }

  return filters;
};

/**
 * Market-only variant for endpoints where salesperson does not apply.
 * Plants have no salesperson, so `GET plants performance` uses this.
 */
export const backlogMarketQueryFilters = <TRow extends { id: string }>(
  markets: string[],
): FilterByOpts<TRow>[] =>
  markets.length > 0 ? [namedFilter<TRow>(MARKET_FILTER_NAME, markets)] : [];

/**
 * Applies the page-level selection to a DataTable model as the AUTHORITATIVE
 * source of `marketId` / `userId`.
 *
 * The removal list is unconditional, so an empty selection clears any filter
 * that was restored into the model from the table's saved localStorage state.
 * That matters: `marketFilter` is a dedicated persisted key restored on mount
 * (useTableState.ts:101-103) and the toolbar Save button re-persists it
 * (useTableState.ts:423-426). A stale `marketId` left in place would AND with
 * the page filter server-side and silently return zero rows.
 *
 * Note this suppresses the stale value at read time; it does NOT delete the
 * localStorage entry, which survives until the next toolbar Save (that Save
 * now reads back `undefined` for marketId, so it self-heals). Suppression is
 * deliberate: it holds on every mount regardless of what is persisted, whereas
 * a one-time delete would be undone the next time anything wrote the key.
 *
 * The `EqualsOrNull` entry clears the nil-filter companion that
 * `makeSetNamedFilter` pairs with `marketId`
 * (LocationMarketDivisionFilters.tsx:66-69).
 *
 * Uses `replaceFilters` so removal and addition are a single state change.
 * Filter removal matches on name + operation and ignores `values`, so the
 * empty `values` arrays below remove whatever is currently applied.
 */
export const syncBacklogFiltersToTable = async <
  TRow extends { id: string },
  TAgg extends { id: string },
>(
  tableModel: TableDataModel<TRow, TAgg>,
  selection: BacklogFilterSelection,
): Promise<void> => {
  const stale: FilterByOpts<TRow>[] = [
    namedFilter<TRow>(MARKET_FILTER_NAME, []),
    {
      name: MARKET_FILTER_NAME,
      operation: Enums.FilterOperation.EqualsOrNull,
      values: [NIL_UUID],
    } as FilterByOpts<TRow>,
    namedFilter<TRow>(SALESPERSON_FILTER_NAME, []),
  ];

  await tableModel.replaceFilters(stale, backlogQueryFilters<TRow>(selection));
};
